import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import type { FormField, FormResponseList, FormResponseRow, FormResponseSchema, WebhookDelivery } from '@/api';
import { DEFAULT_QUERY, ResponsesPanel, type ResponsesQuery } from '@/scenes/Responses/ResponsesPanel';
import type { FieldValue } from '@/scenes/Responses/format';

// Dev only: the Responses sheet of a voice-form session with mocked data (no login needed).
// /dev/responses-preview?theme=dark&state=loading|empty|error

export const Route = createFileRoute('/dev/responses-preview')({
  component: Preview,
  validateSearch: (search: Record<string, unknown>): { theme?: string; state?: string } => ({
    theme: typeof search.theme === 'string' ? search.theme : undefined,
    state: typeof search.state === 'string' ? search.state : undefined,
  }),
});

const FIELDS: FormField[] = [
  { key: 'persona', label: 'Persona', type: 'choice', options: ['Entrepreneur', 'Investor', 'Clinic owner', 'Other'], description: 'Which best describes them' },
  { key: 'new_business', label: 'New business', type: 'boolean', options: [], description: '' },
  { key: 'capital_range', label: 'Capital', type: 'choice', options: ['Under $25k', '$25k–$50k', '$50k–$100k', '$100k+'], description: '' },
  { key: 'capital_available', label: 'Capital ready', type: 'boolean', options: [], description: '' },
  { key: 'launch_timeframe', label: 'Launch', type: 'choice', options: ['Within 3 months', '3–6 months', '6–12 months', '12+ months'], description: '' },
  { key: 'monthly_patient_goal', label: 'Patients / mo', type: 'number', options: [], description: '' },
  { key: 'readiness', label: 'Readiness', type: 'choice', options: ['Ready now', 'Within 30 days', 'Exploring'], description: '' },
  { key: 'decision_makers', label: 'Decision makers', type: 'text', options: [], description: '' },
  { key: 'phone', label: 'Phone', type: 'phone', options: [], description: '' },
  { key: 'best_call_time', label: 'Best time', type: 'text', options: [], description: '' },
];

type P = [string, string, string | null, boolean | null, string | null, boolean | null, string | null, number | null, string | null, string | null, string | null, string | null];
const PEOPLE: P[] = [
  ['Ethelyn', 'Kilback', 'Entrepreneur', true, '$25k–$50k', true, null, null, 'Ready now', null, null, null],
  ['Maya', 'Okafor', 'Clinic owner', true, '$100k+', true, 'Within 3 months', 220, 'Ready now', 'Me and my co-founder', '+14155550142', 'Weekday mornings'],
  ['Daniel', 'Brooks', 'Entrepreneur', true, '$25k–$50k', false, '6–12 months', 60, 'Exploring', 'Just me', '+13125550199', 'After 6pm'],
  ['Priya', 'Raman', 'Investor', true, '$100k+', true, '3–6 months', 500, 'Within 30 days', 'Investment committee', '+447700900321', 'Tuesdays or Thursdays'],
  ['Lucas', 'Moreau', 'Entrepreneur', true, '$50k–$100k', true, '3–6 months', 120, 'Ready now', 'My wife', '+33612345678', 'Lunch time'],
  ['Sofia', 'Lindqvist', 'Clinic owner', false, '$50k–$100k', true, 'Within 3 months', 300, 'Within 30 days', 'Clinic partners (3)', '+46701234567', 'Any afternoon'],
  ['Ethan', 'Park', 'Other', true, 'Under $25k', false, '12+ months', null, 'Exploring', null, null, null],
  ['Amara', 'Nwosu', 'Entrepreneur', true, '$25k–$50k', true, '6–12 months', 80, 'Within 30 days', 'Business partner', '+2348031234567', 'Weekends'],
  ['Jonas', 'Weber', 'Investor', true, null, null, '3–6 months', null, 'Exploring', 'Fund partners', '+4915123456789', 'Mornings CET'],
  ['Grace', 'Kim', 'Clinic owner', true, '$100k+', true, 'Within 3 months', 400, 'Ready now', 'Just me', '+12065550117', 'Before 10am'],
  ['Mateo', 'García', 'Entrepreneur', true, '$50k–$100k', false, '6–12 months', 150, 'Within 30 days', 'My brother', '+34612345678', 'Evenings'],
  ['Hannah', 'Cole', 'Other', null, 'Under $25k', null, null, null, null, null, '+16175550188', null],
  ['Omar', 'Haddad', 'Clinic owner', true, '$100k+', true, '3–6 months', 250, 'Ready now', 'Board of the clinic', '+97150123456', 'Sunday to Thursday'],
];

function seedRows(): FormResponseRow[] {
  const now = Date.now();
  return PEOPLE.map((p, i) => {
    const values: Record<string, FieldValue> = {};
    FIELDS.forEach((f, j) => (values[f.key] = p[j + 2] as FieldValue));
    const at = new Date(now - i * 1.7 * 86400000 - (i === 0 ? 0 : 3600000 * i)).toISOString();
    return {
      id: `app-${i}`,
      name: `${p[0]} ${p[1]}`,
      email: `${p[0].toLowerCase()}.${p[1].toLowerCase()}@example.com`,
      phone: p[10],
      status: i === 1 ? 'generating-summary' : 'completed',
      created_at: at,
      completed_at: at,
      values,
      edited: i === 4 ? ['best_call_time'] : [],
    };
  });
}

function Preview() {
  const { theme = 'light', state } = Route.useSearch();
  const [rows, setRows] = useState<FormResponseRow[]>(seedRows);
  const [query, setQuery] = useState<ResponsesQuery>({ ...DEFAULT_QUERY, pageSize: 10 });
  const [webhookUrl, setWebhookUrl] = useState<string | null>('https://hooks.zapier.com/hooks/catch/123/abc/');
  const [lastDelivery, setLastDelivery] = useState<WebhookDelivery | null>({
    at: new Date(Date.now() - 120000).toISOString(),
    event: 'response.created',
    status_code: 200,
    ok: true,
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const schema: FormResponseSchema = {
    session_id: 'demo',
    session_name: 'Telehealth launch',
    is_voice_form: true,
    fields: FIELDS,
    steps: [],
    webhook: { url: webhookUrl, last_delivery: lastDelivery },
  };

  const list: FormResponseList | undefined = useMemo(() => {
    if (state === 'loading') return undefined;
    let items = state === 'empty' ? [] : rows;
    const term = query.search.toLowerCase();
    if (term) {
      items = items.filter((r) =>
        [r.name, r.email, ...Object.values(r.values ?? {}).flat()].some((v) =>
          String(v ?? '').toLowerCase().includes(term),
        ),
      );
    }
    if (query.status !== 'all') items = items.filter((r) => r.status === query.status);
    if (query.filter) items = items.filter((r) => r.values?.[query.filter!.key] === query.filter!.value);
    const dir = query.order === 'asc' ? 1 : -1;
    items = [...items].sort((a, b) => {
      const av = query.sort.startsWith('field:') ? a.values?.[query.sort.slice(6)] : a.created_at;
      const bv = query.sort.startsWith('field:') ? b.values?.[query.sort.slice(6)] : b.created_at;
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return (av > bv ? 1 : -1) * dir;
    });
    const start = (query.page - 1) * query.pageSize;
    return { items: items.slice(start, start + query.pageSize), total: items.length, page: query.page, page_size: query.pageSize };
  }, [rows, query, state]);

  const save = (row: FormResponseRow, key: string, value: FieldValue) => {
    setRows((all) =>
      all.map((r) =>
        r.id === row.id
          ? { ...r, values: { ...r.values, [key]: value }, edited: Array.from(new Set([...(r.edited ?? []), key])) }
          : r,
      ),
    );
  };

  return (
    <div className="min-h-dvh bg-background px-4 py-6 text-foreground sm:px-8">
      <div className="mx-auto max-w-6xl">
        <h1 className="mb-4 text-lg font-semibold">Telehealth launch</h1>
        <ResponsesPanel
          schema={schema}
          list={list}
          loading={state === 'loading'}
          query={query}
          onQueryChange={setQuery}
          onSaveField={save}
          onOpen={(row) => toast(`Would open ${row.name}`)}
          onExport={() => toast('Would export CSV')}
          webhook={{
            onSave: async (url) => {
              await new Promise((r) => setTimeout(r, 300));
              const created = !webhookUrl && url ? 'whsec_demo_9f2c1a7b4e' : null;
              setWebhookUrl(url);
              return created;
            },
            onTest: async () => {
              await new Promise((r) => setTimeout(r, 500));
              const d: WebhookDelivery = { at: new Date().toISOString(), event: 'response.test', status_code: 200, ok: true };
              setLastDelivery(d);
              return d;
            },
          }}
        />
      </div>
    </div>
  );
}
